import { apiFetch, normalizeCursor } from '../../lib/api';
import type { ProductionGrant } from './productionGrantApi';

export interface AdminOrgSummary {
  org_id: string;
  name: string;
  slug: string;
  state: string;
  owner_user_id: string;
  default_family_id?: string;
  owner_email?: string;
  member_count: number;
  pending_invites: number;
  quota_overrides?: Record<string, unknown>;
  /**
   * 公司登記 master data(common/08 §4.2)。`type=org` 核可時由創始申請人的
   * `developer_profiles.company_info` 促升寫入 org row,成為 audit-facing、
   * **與 owner 無關**的權威來源 —— owner 換人不影響它。
   * 後端一直有回(restful `domain/developer.go`),只是前端從來沒渲染。
   * 舊 row 尚未 backfill 者缺席。
   */
  company_info?: { name?: string; tax_id?: string; country?: string; contact?: string };
  /** ADR-048 organization Production Grant; absent until the admin state machine mints it. */
  production_grant?: ProductionGrant;
  created_at: number;
  updated_at: number;
}

export interface AdminOrgMember {
  user_id: string;
  email?: string;
  display_name?: string;
  org_role: string;
  invited_by?: string;
  joined_at: number;
}

export interface AdminOrgInvite {
  invite_email: string;
  org_role: string;
  state: string;
  invited_by?: string;
  invited_at: number;
  invite_expires_at_s: number;
}

export interface AdminOrgDetail extends AdminOrgSummary {
  members: AdminOrgMember[];
  invites: AdminOrgInvite[];
}

export interface AdminOrgListParams {
  state?: string;
  query?: string;
  cursor?: string;
  limit?: number;
}

export interface AdminOrgListPage {
  items: AdminOrgSummary[];
  nextCursor: string | null;
  limit: number | null;
}

export type AssignableOrgRole = 'admin' | 'developer' | 'viewer';

export interface OrgMemberMutationInput {
  reason?: string;
  case_id?: string;
}

interface AdminOrgListEnvelope {
  items?: AdminOrgSummary[];
  meta?: {
    next_cursor?: string | null;
    limit?: number;
  };
}

export async function listOrganizations(params: AdminOrgListParams = {}): Promise<AdminOrgListPage> {
  const res = await apiFetch<AdminOrgListEnvelope>('/v3/admin/dev/orgs', {
    query: {
      state: params.state,
      q: params.query,
      cursor: params.cursor,
      limit: params.limit,
    },
  });
  return {
    items: Array.isArray(res.data?.items) ? res.data.items : [],
    nextCursor: normalizeCursor(res.data?.meta?.next_cursor),
    limit: res.data?.meta?.limit ?? null,
  };
}

export async function getOrganization(orgId: string): Promise<AdminOrgDetail> {
  const res = await apiFetch<AdminOrgDetail>(`/v3/admin/dev/orgs/${encodeURIComponent(orgId)}`);
  return {
    ...res.data,
    members: Array.isArray(res.data?.members) ? res.data.members : [],
    invites: Array.isArray(res.data?.invites) ? res.data.invites : [],
  };
}

export async function addOrganizationMember(
  orgId: string,
  input: { email: string; org_role: AssignableOrgRole } & OrgMemberMutationInput
): Promise<AdminOrgMember> {
  const res = await apiFetch<AdminOrgMember>(`/v3/admin/dev/orgs/${encodeURIComponent(orgId)}/members`, {
    method: 'POST',
    body: input,
  });
  return res.data;
}

export async function updateOrganizationMemberRole(
  orgId: string,
  userId: string,
  input: { org_role: AssignableOrgRole } & OrgMemberMutationInput
): Promise<AdminOrgMember> {
  const res = await apiFetch<AdminOrgMember>(
    `/v3/admin/dev/orgs/${encodeURIComponent(orgId)}/members/${encodeURIComponent(userId)}`,
    {
      method: 'PATCH',
      body: input,
    }
  );
  return res.data;
}

export async function removeOrganizationMember(orgId: string, userId: string, input: OrgMemberMutationInput = {}): Promise<void> {
  await apiFetch<{ removed: boolean; user_id: string }>(
    `/v3/admin/dev/orgs/${encodeURIComponent(orgId)}/members/${encodeURIComponent(userId)}`,
    {
      method: 'DELETE',
      body: input,
    }
  );
}
